/**
 * Feb 20, 2026 · Terms & Conditions Templates — standalone admin screen.
 *
 * Type-aware: templates carry a `template_type` of either "sales"
 * (surfaces on GST Invoices + Proforma Invoices) or "purchase" (surfaces
 * on Purchase Orders). Each type owns its own default independently so
 * a sales default never leaks into a supplier PO.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { RowActions } from "@/components/RowActions";
import { toast } from "sonner";
import {
  FileTextIcon, FloppyDiskIcon, PlusIcon, PencilSimpleIcon,
  TrashIcon, CheckCircleIcon, StarIcon,
} from "@phosphor-icons/react";

const inp = "w-full h-9 px-2.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";
const area = "w-full min-h-[80px] px-2.5 py-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)] resize-y";

const TYPE_LABEL = { sales: "Sales (Customer)", purchase: "Purchase (Supplier)" };
const TYPE_STYLE = {
  sales:    "border-sky-500/40 text-sky-300 bg-sky-500/5",
  purchase: "border-fuchsia-500/40 text-fuchsia-300 bg-fuchsia-500/5",
};

export default function TncTemplates() {
  return (
    <div className="space-y-6" data-testid="tnc-templates-page">
      <div>
        <div className="overline">Admin</div>
        <h1 className="font-display text-3xl font-bold text-white">Terms &amp; Conditions Templates</h1>
        <div className="text-xs text-zinc-500 mt-1">
          Reusable T&amp;C blocks. <span className="text-sky-300">Sales</span> templates auto-flow to GST Invoices &amp; Proforma Invoices.
          {" "}<span className="text-fuchsia-300">Purchase</span> templates auto-flow to Purchase Orders.
          Each type owns its own default independently.
        </div>
      </div>
      <div className="tactical-card p-5">
        <TncMaster />
      </div>
    </div>
  );
}

function TncMaster() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all"); // all | sales | purchase

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tnc-templates/");
      setRows(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load T&C templates");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const c = { all: rows.length, sales: 0, purchase: 0 };
    for (const r of rows) {
      const t = r.template_type || "sales";
      if (t === "sales" || t === "purchase") c[t] += 1;
    }
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    if (typeFilter === "all") return rows;
    return rows.filter(r => (r.template_type || "sales") === typeFilter);
  }, [rows, typeFilter]);

  const onSetDefault = async (row) => {
    try {
      await api.post(`/tnc-templates/${row.id}/set-default`);
      const t = row.template_type || "sales";
      toast.success(`"${row.name}" is now the default ${TYPE_LABEL[t]} template`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to set default"); }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Delete template "${row.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/tnc-templates/${row.id}`);
      toast.success("Template deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="tnc-master">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <FileTextIcon size={16} className="text-[var(--accent)]" />
          <div className="overline mr-3">Templates</div>
          {[
            { key: "all",      label: "All",      count: counts.all },
            { key: "sales",    label: "Sales",    count: counts.sales },
            { key: "purchase", label: "Purchase", count: counts.purchase },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTypeFilter(t.key)}
              data-testid={`tnc-filter-${t.key}`}
              className={`h-7 px-2.5 rounded-sm border text-[10px] uppercase tracking-widest font-mono ${
                typeFilter === t.key
                  ? "border-[var(--accent)] bg-[var(--accent)]/10 text-white"
                  : "border-[var(--border)] text-zinc-500 hover:text-white"
              }`}
            >
              {t.label} · {t.count}
            </button>
          ))}
        </div>
        <button
          onClick={() => setEditing({ template_type: typeFilter === "purchase" ? "purchase" : "sales" })}
          data-testid="tnc-add-btn"
          className="h-9 px-3 rounded-sm bg-[var(--accent)] hover:bg-blue-600 text-white text-xs font-semibold flex items-center gap-1.5"
        >
          <PlusIcon size={12} weight="bold" /> New Template
        </button>
      </div>

      {loading ? (
        <div className="text-xs text-zinc-500">Loading templates…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-[var(--border)] rounded-sm p-6 text-center text-xs text-zinc-500" data-testid="tnc-empty">
          No {typeFilter === "all" ? "" : `${typeFilter} `}templates yet. Create the first one to get started.
        </div>
      ) : (
        <div className="overflow-x-auto border border-[var(--border)] rounded-sm">
          <table className="w-full text-xs" data-testid="tnc-table">
            <thead className="bg-[var(--bg-0)] text-zinc-500 uppercase tracking-widest text-[10px]">
              <tr>
                <th className="px-3 py-2 text-left w-8"></th>
                <th className="px-3 py-2 text-left w-40">Type</th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Preview</th>
                <th className="px-3 py-2 text-right w-40">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const t = r.template_type || "sales";
                return (
                <tr key={r.id} className="border-t border-[var(--border)] hover:bg-white/[0.02]" data-testid={`tnc-row-${r.id}`}>
                  <td className="px-3 py-2">
                    {r.is_default ? (
                      <span title={`Default ${TYPE_LABEL[t]}`} className="inline-flex" data-testid={`tnc-default-badge-${r.id}`}>
                        <StarIcon size={14} weight="fill" className="text-amber-400" />
                      </span>
                    ) : (
                      <button
                        title={`Mark as default ${TYPE_LABEL[t]} template`}
                        onClick={() => onSetDefault(r)}
                        data-testid={`tnc-set-default-${r.id}`}
                        className="text-zinc-600 hover:text-amber-400"
                      >
                        <StarIcon size={14} />
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[9px] uppercase tracking-widest font-mono ${TYPE_STYLE[t]}`}>
                      {TYPE_LABEL[t]}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-semibold text-white">
                    {r.name}
                    {r.is_default && (
                      <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border border-amber-500/40 text-amber-300 bg-amber-500/5 text-[9px] uppercase tracking-widest">
                        <CheckCircleIcon size={9} weight="fill" /> Default
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-zinc-400 max-w-xl truncate">
                    {(r.text || "").split("\n")[0].slice(0, 120)}{(r.text || "").length > 120 ? "…" : ""}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <RowActions
                      testid={`tnc-actions-${r.id}`}
                      items={[
                        { label: "Edit", testid: `tnc-edit-${r.id}`, onClick: () => setEditing(r) },
                        { label: "Delete", tone: "danger", testid: `tnc-delete-${r.id}`, onClick: () => onDelete(r) },
                      ]}
                    />
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <TncEditor
          initial={editing.id ? editing : null}
          initialType={editing.template_type || "sales"}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function TncEditor({ initial, initialType, onClose, onSaved }) {
  const isNew = !initial;
  const [name, setName] = useState(initial?.name || "");
  const [text, setText] = useState(initial?.text || "");
  const [templateType, setTemplateType] = useState(initial?.template_type || initialType || "sales");
  const [isDefault, setIsDefault] = useState(!!initial?.is_default);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const n = name.trim(), t = text.trim();
    if (!n || !t) { toast.error("Name and text are required"); return; }
    setSaving(true);
    try {
      const payload = { name: n, text: t, template_type: templateType, is_default: isDefault };
      if (isNew) {
        await api.post("/tnc-templates/", payload);
        toast.success("Template created");
      } else {
        await api.put(`/tnc-templates/${initial.id}`, payload);
        toast.success("Template updated");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="tnc-editor">
      <div className="bg-[var(--bg-1)] border border-[var(--border)] rounded-sm w-full max-w-2xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-[var(--border)] flex items-center justify-between">
          <div className="font-display text-lg font-semibold text-white">
            {isNew ? "New T&C Template" : `Edit — ${initial.name}`}
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white text-lg leading-none">×</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="overline mb-1.5">Template Name *</div>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Domestic Standard, Yarn Supplier"
                className={inp}
                data-testid="tnc-editor-name"
                autoFocus
              />
            </div>
            <div>
              <div className="overline mb-1.5">Template Type *</div>
              <select
                value={templateType}
                onChange={e => setTemplateType(e.target.value)}
                className={inp}
                data-testid="tnc-editor-type"
              >
                <option value="sales">{TYPE_LABEL.sales}</option>
                <option value="purchase">{TYPE_LABEL.purchase}</option>
              </select>
              <div className="text-[10px] text-zinc-500 mt-1">
                {templateType === "sales"
                  ? "Auto-flows to GST Invoices & Proforma Invoices"
                  : "Auto-flows to Purchase Orders"}
              </div>
            </div>
          </div>
          <div>
            <div className="overline mb-1.5">Terms &amp; Conditions Text *</div>
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              rows={10}
              placeholder={"1. Payment due within 30 days from invoice date.\n2. Interest @ 18% p.a. on overdue balances.\n3. Goods once sold cannot be returned…"}
              className={`${area} min-h-[220px] font-mono`}
              data-testid="tnc-editor-text"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={e => setIsDefault(e.target.checked)}
              data-testid="tnc-editor-default"
            />
            <StarIcon size={12} className="text-amber-400" />
            Set as default {TYPE_LABEL[templateType]} template
          </label>
        </div>
        <div className="px-5 py-3 border-t border-[var(--border)] flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="h-9 px-3 rounded-sm border border-[var(--border)] text-xs hover:bg-white/[0.03]"
            data-testid="tnc-editor-cancel"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="tnc-editor-save"
            className="h-9 px-4 rounded-sm bg-[var(--accent)] hover:bg-blue-600 text-white text-xs font-semibold flex items-center gap-2 disabled:opacity-50"
          >
            <FloppyDiskIcon size={12} weight="bold" /> {saving ? "Saving…" : "Save Template"}
          </button>
        </div>
      </div>
    </div>
  );
}
